import { useCallback, useEffect, useRef, useState } from 'react';
import { useTRPCAPI, useTRPCClientAPI, useTRPCQuery } from '../../../../trpc/trpc';
import { trackEvent } from '../../../analytics/sml/analytics';
import { useSettingsSaveState } from '../useSettingsSaveState';

/** Server enum `daily | weekly | off`, mirrored so the bundle does not import the Prisma client. */
export type ReportFrequency = 'daily' | 'weekly' | 'off';
export type AlertThresholdPercent = 10 | 20 | 40;
export type PlaylistAlertMinFollowers = 1000 | 10000 | 100000;

export interface NotificationPreferences {
  reportFrequency: ReportFrequency;
  alertsEnabled: boolean;
  alertThresholdPercent: AlertThresholdPercent;
  playlistAlertsEnabled: boolean;
  playlistAlertMinFollowers: PlaylistAlertMinFollowers;
  planEmailsEnabled: boolean;
  postPackEmailsEnabled: boolean;
  /**
   * Outreach replies and placements (push category `outreach`, column
   * `UserModel.music24PushOutreachEnabled`, plan 2026-10-11 Task 8). A server without the field
   * answers nothing: the switch then shows on, the column default.
   */
  pushOutreachEnabled: boolean;
  /**
   * The pre-save milestone emails (email list `presaveMilestones`, column
   * `UserModel.music24PresaveMilestoneEmailsEnabled`, owner decision 2026-10-14). A server without
   * the field answers nothing: the switch then shows on, the column default.
   */
  presaveMilestoneEmailsEnabled: boolean;
  /**
   * The release notes mail (email list `productUpdates`, column
   * `UserModel.music24ProductUpdatesEmailsEnabled`, spec 2026-10-20 What's new §8). A server without
   * the field answers nothing: the switch then shows on, the column default.
   */
  productUpdates: boolean;
}

export type NotificationPreferenceKey = keyof NotificationPreferences;

/**
 * The notification settings, saved one control at a time (design settings-redesign c1): each change
 * sends only its own field to users.updateNotificationPreferences. A failed save puts the old value
 * back and shows the error in the tab head.
 */
export function useNotificationPreferences() {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const query = useTRPCQuery(trpcAPI.users.getNotificationPreferences.queryOptions(undefined, { retry: false }));
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const save = useSettingsSaveState();
  const pending = useRef(0);

  useEffect(() => {
    if (query.data === undefined) {
      return;
    }
    setPrefs({
      reportFrequency: query.data.reportFrequency as ReportFrequency,
      alertsEnabled: query.data.alertsEnabled,
      alertThresholdPercent: query.data.alertThresholdPercent as AlertThresholdPercent,
      playlistAlertsEnabled: query.data.playlistAlertsEnabled,
      playlistAlertMinFollowers: query.data.playlistAlertMinFollowers as PlaylistAlertMinFollowers,
      planEmailsEnabled: query.data.planEmailsEnabled,
      postPackEmailsEnabled: query.data.postPackEmailsEnabled,
      pushOutreachEnabled: (query.data as { pushOutreachEnabled?: boolean }).pushOutreachEnabled ?? true,
      presaveMilestoneEmailsEnabled:
        (query.data as { presaveMilestoneEmailsEnabled?: boolean }).presaveMilestoneEmailsEnabled ?? true,
      productUpdates: (query.data as { productUpdates?: boolean }).productUpdates ?? true,
    });
  }, [query.data]);

  const { setState } = save;
  const update = useCallback(
    <K extends NotificationPreferenceKey>(key: K, value: NotificationPreferences[K]) => {
      if (prefs === null || prefs[key] === value) {
        return;
      }
      const previous = prefs[key];
      setPrefs((current) => (current === null ? current : { ...current, [key]: value }));
      pending.current += 1;
      setState('saving');
      trpcClient.users.updateNotificationPreferences
        .mutate({ [key]: value })
        .then(() => {
          pending.current -= 1;
          if (pending.current === 0) {
            setState('saved');
          }
          trackEvent('notification_setting_changed', { setting: key, on: value !== false && value !== 'off' });
        })
        .catch(() => {
          pending.current -= 1;
          setPrefs((current) => (current === null ? current : { ...current, [key]: previous }));
          setState('failed');
        });
    },
    [prefs, setState, trpcClient]
  );

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    refetchQuery().catch(console.error);
  }, [refetchQuery]);

  return {
    prefs,
    email: query.data?.email ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch,
    saveState: save.state,
    /** For a row that saves through another call (the SongsBrew switch): it shares the tab head status. */
    setSaveState: setState,
    update,
  };
}
